import {
  useMemo,
  useEffect,
  useRef,
  useLayoutEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { Fade, makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import UserAvatar from '../userAvatar';
import DraggableScroll from '../draggableScroll';
import ScrollIndicators from '../scrollIndicators';
import ToggleComponent from '../toggleComponent';
import ActionButton from '../actionButton';
import { ReactComponent as RemoveIcon } from '../../../assets/icons/close-gray.svg';
import { getPersonShortName, isObjectEmpty } from '../../../utility/helpers';
import {
  usePreviousValue,
  useDraggableScrollPosition,
} from '../../../utility/hooks';
import { updateScrollPosition } from '../../../utility/fillSurveyUtils';
import { ACTION_BUTTON_TYPES } from '../actionButton/config';

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    main: {
      boxSizing: 'border-box',
      alignItems: 'center',
      overflowX: 'hidden',
      width: '100%',
      padding: spacing(0, 0, 2, 2),
      position: 'relative',
      maxWidth: '100%',
      [breakpoints.up('sm')]: {
        padding: spacing(0, 0, 4, 4),
      },
    },
    users: {
      display: 'flex',
      alignItems: 'center',
      minHeight: 30,
      '& .selected': {
        backgroundColor: primary.blue6,
        '&$userDisabled': {
          backgroundColor: primary.bluish8,
        },
      },
      [breakpoints.up('sm')]: {
        minHeight: 40,
      },
    },
    user: {
      border: `1px solid ${primary.bluish6}`,
      borderRadius: 60,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      padding: spacing(1, 2),
      marginRight: spacing(1),
      pointerEvents: 'auto !important',
      '&:last-of-type': {
        marginRight: spacing(2),
      },
      [breakpoints.up('sm')]: {
        padding: spacing(2, 4),
        marginRight: spacing(2),
        '&:last-of-type': {
          marginRight: spacing(4),
        },
      },
    },
    userDisabled: {
      cursor: 'default',
    },
    userAvatar: {
      height: 20,
      width: 20,
      fontSize: 7,
      lineHeight: '18px',
      [breakpoints.up('sm')]: {
        fontSize: 14,
        lineHeight: 1,
        height: 32,
        width: 32,
      },
    },
    userLabel: {
      fontFamily: 'ProximaNova-Bold',
      fontSize: 12,
      lineHeight: '14px',
      whiteSpace: 'nowrap',
      [breakpoints.up('sm')]: {
        fontSize: 16,
        lineHeight: '18px',
      },
    },
    removeIcon: {
      flexShrink: 0,
      height: 8,
      width: 8,
      marginLeft: spacing(1),
      [breakpoints.up('sm')]: {
        height: 14,
        width: 14,
        marginLeft: spacing(3),
      },
    },
    draggableScroll: {
      display: 'flex',
      paddingTop: spacing(10),
    },
    usersListWrapper: {
      width: 'auto',
    },
    usersWrapper: {
      display: 'flex',
    },
    arrow: {
      width: 24,
      height: 24,
      [breakpoints.up('xLg')]: {
        width: 24,
        height: 24,
      },
    },
    arrowLeft: {
      left: 8,
      top: 8,
      [breakpoints.up('sm')]: {
        left: 16,
      },
    },
    arrowRight: {
      right: 8,
      top: 8,
      [breakpoints.up('sm')]: {
        right: 16,
      },
    },
    addCommentButton: {
      width: 32,
      height: 32,
      marginRight: spacing(1),
    },
    commentIconDisabled: {
      '& path': {
        fill: primary.bluish7,
      },
    },
    addIcon: {
      width: 20,
      height: 20,
    },
    editIcon: {
      width: 20,
      height: 18,
    },
  })
);

const SelectableUserList = ({
  className = null,
  users,
  selection = {},
  questionId = 0,
  isDisabled = false,
  hasAddCommentButton = false,
  isInsightsView = false,
  isUserSelectedCheck = () => false,
  shouldRenderUser = () => true,
  onSelect = () => {},
  onAddComment = () => {},
}) => {
  const classes = useStyles();
  const draggableScrollRef = useRef(null);
  const usersRef = useRef(null);
  const previousQuestionId = usePreviousValue(questionId);
  const [
    activeScroll,
    onChangeScrollPosition,
    onScrollToRight,
    ,
    onScrollToStart,
  ] = useDraggableScrollPosition(draggableScrollRef, usersRef);

  const selectableUsers = useMemo(() => {
    return users.filter(user => shouldRenderUser(user));
  }, [users, shouldRenderUser]);

  const handleSelect = useCallback(
    (user, isSelected) => () => onSelect(user, isSelected),
    [onSelect]
  );

  const handleAddComment = (e, user) => {
    e.stopPropagation();
    onAddComment(user, questionId);
  };

  useEffect(() => {
    if (previousQuestionId !== questionId) {
      onChangeScrollPosition();
      updateScrollPosition(draggableScrollRef.current, false);
    }
  }, [questionId, previousQuestionId, onChangeScrollPosition]);

  useLayoutEffect(() => {
    onChangeScrollPosition();
  }, [selection, onChangeScrollPosition]);

  const handleScrollEnd = () => onChangeScrollPosition();

  return (
    <div className={classNames(classes.main, className)}>
      <DraggableScroll
        ref={draggableScrollRef}
        className={classNames(classes.draggableScroll, classes.users)}
        onEndScroll={handleScrollEnd}
      >
        <div ref={usersRef} className={classes.usersListWrapper}>
          <div className={classes.usersWrapper}>
            {selectableUsers.map(user => {
              const isSelected = isUserSelectedCheck(user);
              const answer =
                user.replied && !isObjectEmpty(user.replied)
                  ? user.replied[questionId]
                  : { reply: '' };
              const isEditComment = !!answer?.reply;

              return (
                <div
                  key={`user_${user.id}`}
                  className={classNames(classes.user, {
                    [classes.userDisabled]: isDisabled,
                    selected: isSelected,
                  })}
                  onClick={handleSelect(user, isSelected)}
                >
                  <Fade
                    in={
                      isSelected &&
                      hasAddCommentButton &&
                      !(isInsightsView && !isEditComment)
                    }
                    unmountOnExit
                  >
                    <div>
                      <ToggleComponent inKey={isEditComment}>
                        {isEditComment ? (
                          <ActionButton
                            iconButtonClass={classes.addCommentButton}
                            iconClass={classNames(classes.editIcon, {
                              [classes.commentIconDisabled]:
                                !isInsightsView && (isDisabled || !isSelected),
                            })}
                            type={ACTION_BUTTON_TYPES.EDIT_COMMENT}
                            isDisabled={
                              !isInsightsView && (isDisabled || !isSelected)
                            }
                            onClickHandler={e => handleAddComment(e, user)}
                          />
                        ) : (
                          <ActionButton
                            iconButtonClass={classes.addCommentButton}
                            iconClass={classNames(classes.addIcon, {
                              [classes.commentIconDisabled]:
                                isDisabled || !isSelected,
                            })}
                            type={ACTION_BUTTON_TYPES.ADD_COMMENT}
                            isDisabled={isDisabled || !isSelected}
                            onClickHandler={e => handleAddComment(e, user)}
                          />
                        )}
                      </ToggleComponent>
                    </div>
                  </Fade>
                  <UserAvatar
                    customAvatarClass={classes.userAvatar}
                    labelClass={classes.userLabel}
                    getCustomCaption={getPersonShortName}
                    user={user}
                    caption
                  />
                  {isSelected && !isDisabled && (
                    <RemoveIcon className={classes.removeIcon} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </DraggableScroll>
      <ScrollIndicators
        arrowClass={classes.arrow}
        arrowLeftClass={classes.arrowLeft}
        arrowRightClass={classes.arrowRight}
        activeScroll={activeScroll}
        onScrollToRight={onScrollToRight}
        onScrollToStart={onScrollToStart}
      />
    </div>
  );
};

SelectableUserList.propTypes = {
  className: PropTypes.string,
  users: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  questionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selection: PropTypes.shape({}),
  isDisabled: PropTypes.bool,
  hasAddCommentButton: PropTypes.bool,
  isInsightsView: PropTypes.bool,
  isUserSelectedCheck: PropTypes.func,
  shouldRenderUser: PropTypes.func,
  onSelect: PropTypes.func,
  onAddComment: PropTypes.func,
};

export default SelectableUserList;
